import type { ReactNode } from "react";
import { Delta, MetricCard, cn, type MetricDelta } from "@fox1ck-org/ui";

import { formatInt, formatMoney, formatPct } from "@/lib/format";
import { useT } from "@/lib/i18n";

import { metricDelta } from "./campaign-table";
import type { ListColumn } from "./list-columns";
import type { Bag } from "./list-rows";

/**
 * The eight numbers over the table (board CampList): клики, лиды, апрувы,
 * в холде, доход, расход, профит, ROI — the server's «Итого» of exactly the
 * campaigns the table lists, with the change against the Top bar's
 * comparison. Every figure is a key of the same totals the «Итого» line
 * reads, so the strip and the line never disagree.
 *
 * `narrow` (board Camp1280): six — the hold and the revenue step aside so no
 * tile's line is cut; they are still a column away in «Колонки».
 */
export function CampaignKpis({
  v,
  prev,
  loading,
  column,
  narrow,
}: {
  v: Bag | undefined;
  prev: Bag | undefined;
  loading: boolean;
  /** The schema's column for a key — its label, description and better direction. */
  column: (key: string) => ListColumn | undefined;
  narrow?: boolean;
}) {
  const t = useT();
  const cur = v ?? {};
  const d = (key: string): MetricDelta | undefined => {
    const c = column(key);
    return c && prev ? metricDelta(c, cur[key], prev[key]) : undefined;
  };
  const tiles: { key: string; label: string; value: string; delta?: MetricDelta; note?: string; tone?: "ok" | "danger"; sum?: boolean }[] = [
    {
      key: "clicks",
      label: t("cols.clicks"),
      value: formatInt(cur.clicks),
      delta: d("clicks"),
      note: cur.bot_share !== undefined ? t("camps.kpi.bots", { pct: formatPct(cur.bot_share) }) : undefined,
    },
    { key: "leads", label: t("cols.leads"), value: formatInt(cur.leads), delta: d("leads"), note: t("camps.kpi.cr", { cr: formatPct(cur.cr) }) },
    {
      key: "conversions",
      label: t("cols.approved"),
      value: formatInt(cur.conversions),
      note: t("camps.kpi.approve", { pct: formatPct(cur.approve_rate, { digits: 0 }) }),
    },
    {
      key: "conversions_hold",
      label: t("camps.kpi.hold"),
      value: formatInt(cur.conversions_hold),
      note: cur.revenue_in_transit !== undefined ? t("camps.kpi.inFlight", { sum: formatMoney(cur.revenue_in_transit) }) : undefined,
    },
    {
      key: "revenue",
      label: t("cols.revenue"),
      value: formatMoney(cur.revenue),
      delta: d("revenue"),
      note: t("camps.kpi.epc", { epc: formatMoney(cur.epc, { unit: true }) }),
    },
    {
      key: "cost",
      label: t("cols.cost"),
      value: formatMoney(cur.cost),
      delta: d("cost"),
      note: t("camps.kpi.cpa", { cpa: formatMoney(cur.cpa, { unit: true }) }),
    },
    {
      key: "profit",
      label: t("cols.profit"),
      value: formatMoney(cur.profit, { signed: true }),
      delta: d("profit"),
      note: prev ? t("camps.kpi.was", { value: formatMoney(prev.profit, { signed: true }) }) : undefined,
      tone: toneOf(cur.profit),
      sum: true,
    },
    {
      key: "roi",
      label: t("cols.roi"),
      value: formatPct(cur.roi, { digits: 0, signed: true }),
      delta: d("roi"),
      tone: toneOf(cur.roi),
      sum: true,
    },
  ];
  const shown = narrow ? tiles.filter((k) => k.key !== "conversions_hold" && k.key !== "revenue") : tiles;
  return (
    <div className="grid shrink-0 auto-cols-fr grid-flow-col overflow-hidden rounded-lg border border-border bg-card">
      {shown.map((k) => (
        <MetricCard
          labelCase="none"
          key={k.key}
          label={k.label}
          value={k.value}
          loading={loading}
          tone={k.tone}
          comparison={<Comparison delta={k.delta} note={k.note} />}
          className={cn(
            "min-w-0 rounded-none border-0 border-l border-border px-3.5 pb-3 pt-2.5 first:border-l-0",
            k.sum ? "bg-surface-2" : "bg-transparent",
          )}
        />
      ))}
    </div>
  );
}

function toneOf(v: number | undefined): "ok" | "danger" | undefined {
  return v === undefined || v === 0 ? undefined : v > 0 ? "ok" : "danger";
}

function Comparison({ delta, note }: { delta?: MetricDelta; note?: ReactNode }) {
  if (!delta && !note) return <span aria-hidden>&nbsp;</span>;
  return (
    <span className="flex min-w-0 items-baseline gap-1.5 overflow-hidden whitespace-nowrap">
      {delta ? <Delta {...delta} compact icon={false} /> : null}
      {note ? <span className="min-w-0 truncate">{note}</span> : null}
    </span>
  );
}
